import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ConnectionMode,
  MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import FlowNode from "./FlowNode";
import { NODE_TYPES, nodeCfg } from "./nodeConfig";
import { ArrowLeft, Plus, Search, X, Save, Route, Copy } from "lucide-react";

const nodeTypes = { flowNode: FlowNode };
const DELETE_KEYS = ["Backspace", "Delete"];
const DEFAULT_EDGE_OPTIONS = { markerEnd: { type: MarkerType.ArrowClosed } };
const PRO_OPTIONS = { hideAttribution: true };
const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));
const isMaterialKind = (k) => k === "raw_material" || k === "raw_material_cat";
const newEdgeData = () => ({ material: "", quantity: "", unit: "", frequency: "", transport: "", notes: "" });

function Editor({ map, onBack, onNameChange }) {
  const [name, setName] = useState(map.name);
  const [nodes, setNodes, onNodesChange] = useNodesState(map.nodes || []);
  const [edges, setEdges, onEdgesChange] = useEdgesState(map.edges || []);
  const [selected, setSelected] = useState(null); // {kind:'node'|'edge', id}
  const [addKind, setAddKind] = useState("raw_material");
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [trace, setTrace] = useState(null); // {nodeSet, edgeSet}
  const [status, setStatus] = useState("saved");
  const [clipboard, setClipboard] = useState(null);
  const rf = useReactFlow();
  const firstRun = useRef(true);

  const onConnect = useCallback((params) => {
    setEdges((eds) => addEdge({ ...params, id: uid(), type: "default", markerEnd: { type: MarkerType.ArrowClosed }, data: newEdgeData(), label: "" }, eds));
  }, [setEdges]);

  const addNode = () => {
    const id = uid();
    const c = rf.screenToFlowPosition ? rf.screenToFlowPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 }) : { x: 250, y: 180 };
    const n = nodes.length;
    const pos = { x: c.x - 200 + (n % 4) * 180, y: c.y - 120 + Math.floor(n / 4) * 150 };
    setNodes((nds) => [...nds, { id, type: "flowNode", position: pos, data: { label: "", kind: addKind, notes: "" } }]);
    setSelected({ kind: "node", id });
  };

  const duplicateNode = useCallback((node) => {
    if (!node) return;
    const id = uid();
    const pos = { x: (node.position?.x || 0) + 40, y: (node.position?.y || 0) + 40 };
    const { dim, match, ...rest } = node.data || {};
    setNodes((nds) => [...nds, { id, type: "flowNode", position: pos, data: { ...rest } }]);
    setSelected({ kind: "node", id });
  }, [setNodes]);

  const updateNodeData = (id, patch) =>
    setNodes((nds) => nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...patch } } : n)));
  const updateEdgeData = (id, patch) =>
    setEdges((eds) => eds.map((e) => (e.id === id ? { ...e, data: { ...e.data, ...patch }, label: (patch.material ?? e.data?.material) || "" } : e)));

  const traceFrom = (startId) => {
    const adj = {};
    edges.forEach((e) => { (adj[e.source] = adj[e.source] || []).push(e); });
    const nodeSet = new Set([startId]); const edgeSet = new Set(); const q = [startId];
    while (q.length) {
      const cur = q.shift();
      (adj[cur] || []).forEach((e) => { edgeSet.add(e.id); if (!nodeSet.has(e.target)) { nodeSet.add(e.target); q.push(e.target); } });
    }
    return { nodeSet, edgeSet };
  };

  const onNodeClick = (_, node) => {
    setSelected({ kind: "node", id: node.id });
    if (node.data.kind === "raw_material" || node.data.kind === "raw_material_cat") setTrace(traceFrom(node.id));
    else setTrace(null);
  };
  const onEdgeClick = (_, edge) => { setSelected({ kind: "edge", id: edge.id }); };
  const onPaneClick = () => { setSelected(null); setTrace(null); };

  // Save (debounced autosave)
  const save = useCallback(async (silent = true) => {
    setStatus("saving");
    try {
      const cleanNodes = nodes.map(({ id, position, data }) => ({ id, type: "flowNode", position, data: { label: data.label, kind: data.kind, notes: data.notes || "", processType: data.processType || "", machine: data.machine || "", location: data.location || "", variant: data.variant || "" } }));
      const cleanEdges = edges.map(({ id, source, target, sourceHandle, targetHandle, data }) => ({ id, source, target, sourceHandle, targetHandle, type: "default", markerEnd: { type: MarkerType.ArrowClosed }, data, label: data?.material || "" }));
      await api.put(`/flow-maps/${map.id}`, { name, nodes: cleanNodes, edges: cleanEdges });
      setStatus("saved");
      onNameChange?.(name);
    } catch (e) {
      setStatus("error");
      if (!silent) toast.error("Failed to save map");
    }
  }, [nodes, edges, name, map.id, onNameChange]);

  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    setStatus("unsaved");
    const t = setTimeout(() => save(true), 900);
    return () => clearTimeout(t);
  }, [nodes, edges, name]); // eslint-disable-line

  // Copy / paste selected node with keyboard
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      const k = e.key.toLowerCase();
      if (k === "c" && selected?.kind === "node") {
        const n = nodes.find((x) => x.id === selected.id);
        if (n) setClipboard(n);
      } else if (k === "v" && clipboard) {
        e.preventDefault();
        duplicateNode(clipboard);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected, nodes, clipboard, duplicateNode]);

  const q = query.trim().toLowerCase();
  const matchIds = useMemo(() => {
    if (!q) return null;
    const ids = new Set();
    nodes.forEach((n) => { if ((n.data.label || "").toLowerCase().includes(q) || nodeCfg(n.data.kind).label.toLowerCase().includes(q)) ids.add(n.id); });
    edges.forEach((e) => { if ((e.data?.material || "").toLowerCase().includes(q)) { ids.add(e.source); ids.add(e.target); } });
    return ids;
  }, [q, nodes, edges]);

  const displayNodes = useMemo(() => nodes.map((n) => {
    const passType = typeFilter === "all" || n.data.kind === typeFilter;
    const passTrace = !trace || trace.nodeSet.has(n.id);
    const passSearch = !matchIds || matchIds.has(n.id);
    const dim = !(passType && passTrace && passSearch);
    return { ...n, data: { ...n.data, dim, match: !!(matchIds && matchIds.has(n.id)) } };
  }), [nodes, typeFilter, trace, matchIds]);

  const displayEdges = useMemo(() => edges.map((e) => {
    const on = (!trace || trace.edgeSet.has(e.id)) && (typeFilter === "all");
    const dimmed = (trace && !trace.edgeSet.has(e.id)) || (typeFilter !== "all");
    return { ...e, animated: !!(trace && trace.edgeSet.has(e.id)), style: { opacity: dimmed ? 0.12 : 1, stroke: trace && trace.edgeSet.has(e.id) ? "#f59e0b" : "#94a3b8", strokeWidth: trace && trace.edgeSet.has(e.id) ? 2.5 : 1.5 }, labelStyle: { fontSize: 11, fill: "#475569" }, labelBgStyle: { fill: "#fff" } };
  }), [edges, trace, typeFilter]);

  const selNode = selected?.kind === "node" ? nodes.find((n) => n.id === selected.id) : null;
  const selEdge = selected?.kind === "edge" ? edges.find((e) => e.id === selected.id) : null;
  const nodeName = (id) => nodes.find((n) => n.id === id)?.data.label || "Untitled";
  const selConns = useMemo(() => {
    if (!selNode) return { incoming: [], outgoing: [] };
    const name = (id) => nodes.find((n) => n.id === id)?.data.label || "Untitled";
    return {
      incoming: edges.filter((e) => e.target === selNode.id).map((e) => ({ label: name(e.source), material: e.data?.material })),
      outgoing: edges.filter((e) => e.source === selNode.id).map((e) => ({ label: name(e.target), material: e.data?.material })),
    };
  }, [edges, nodes, selNode]);

  return (
    <div className="flex h-[calc(100vh-1px)] w-full flex-col bg-slate-50" data-testid="flow-editor">
      {/* Top toolbar */}
      <div className="z-10 flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-4 py-2.5">
        <button onClick={() => { save(false); onBack(); }} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="flow-back-button">
          <ArrowLeft className="h-4 w-4" /> Maps
        </button>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 w-56 rounded-sm font-semibold" data-testid="flow-map-name" />
        <div className="flex items-center gap-1.5">
          <Select value={addKind} onValueChange={setAddKind}>
            <SelectTrigger className="h-8 w-52 rounded-sm" data-testid="flow-node-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {NODE_TYPES.map((t) => (
                <SelectItem key={t.key} value={t.key}>
                  <span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: t.color }} />{t.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={addNode} size="sm" className="h-8 rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="flow-add-node-button"><Plus className="mr-1 h-4 w-4" /> Add node</Button>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-2 h-4 w-4 text-slate-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name or material…" className="h-8 w-56 rounded-sm pl-8" data-testid="flow-search-input" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-8 w-44 rounded-sm" data-testid="flow-type-filter"><SelectValue placeholder="Filter type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {NODE_TYPES.map((t) => (<SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>))}
          </SelectContent>
        </Select>
        {trace && (
          <button onClick={() => setTrace(null)} className="inline-flex items-center gap-1 rounded-sm bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700" data-testid="flow-clear-trace"><Route className="h-3.5 w-3.5" /> Tracing · clear</button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-400" data-testid="flow-save-status">{status === "saving" ? "Saving…" : status === "unsaved" ? "Unsaved" : status === "error" ? "Save failed" : "Saved"}</span>
          <Button onClick={() => save(false)} size="sm" variant="outline" className="h-8 rounded-sm" data-testid="flow-save-button"><Save className="mr-1 h-4 w-4" /> Save</Button>
        </div>
      </div>

      <div className="relative flex-1">
        <ReactFlow
          nodes={displayNodes}
          edges={displayEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onEdgeClick={onEdgeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          connectionMode={ConnectionMode.Loose}
          deleteKeyCode={DELETE_KEYS}
          defaultEdgeOptions={DEFAULT_EDGE_OPTIONS}
          fitView
          minZoom={0.1}
          maxZoom={2.5}
          proOptions={PRO_OPTIONS}
        >
          <Background gap={20} color="#e2e8f0" />
          <Controls />
          <MiniMap nodeColor={(n) => nodeCfg(n.data?.kind).color} pannable zoomable />
        </ReactFlow>

        {/* Side panel */}
        {(selNode || selEdge) && (
          <div className="absolute right-0 top-0 flex h-full w-80 flex-col overflow-y-auto border-l border-slate-200 bg-white shadow-lg" data-testid="flow-side-panel">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <h3 className="font-heading font-bold tracking-tight text-slate-900">{selNode ? "Node details" : "Connection"}</h3>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700" data-testid="flow-panel-close"><X className="h-4 w-4" /></button>
            </div>

            {selNode && (
              <div className="space-y-4 p-4">
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{selNode.data.kind === "process" ? "Process Name" : selNode.data.kind === "raw_material" ? "Component Type" : "Name"}</Label>
                  <Input value={selNode.data.label} onChange={(e) => updateNodeData(selNode.id, { label: e.target.value })} className="rounded-sm" data-testid="flow-node-name-input" autoFocus />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Type</Label>
                  <Select value={selNode.data.kind} onValueChange={(v) => updateNodeData(selNode.id, { kind: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="flow-node-kind-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{NODE_TYPES.map((t) => (<SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                {selNode.data.kind === "raw_material" && (
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Variant</Label>
                    <Input value={selNode.data.variant || ""} onChange={(e) => updateNodeData(selNode.id, { variant: e.target.value })} className="rounded-sm" placeholder="e.g. 500ml PET Clear" data-testid="flow-component-variant" />
                  </div>
                )}
                {selNode.data.kind === "process" && (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Process Type</Label>
                      <Select value={selNode.data.processType || ""} onValueChange={(v) => updateNodeData(selNode.id, { processType: v })}>
                        <SelectTrigger className="rounded-sm" data-testid="flow-process-type"><SelectValue placeholder="Select type…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Transformation">Transformation</SelectItem>
                          <SelectItem value="Assembly">Assembly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Machine</Label>
                      <Input value={selNode.data.machine || ""} onChange={(e) => updateNodeData(selNode.id, { machine: e.target.value })} className="rounded-sm" placeholder="e.g. Blow Molder #1" data-testid="flow-process-machine" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Location</Label>
                      <Input value={selNode.data.location || ""} onChange={(e) => updateNodeData(selNode.id, { location: e.target.value })} className="rounded-sm" placeholder="e.g. Pennsylvania Factory" data-testid="flow-process-location" />
                    </div>
                  </>
                )}
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Notes</Label>
                  <Textarea value={selNode.data.notes || ""} onChange={(e) => updateNodeData(selNode.id, { notes: e.target.value })} className="rounded-sm" data-testid="flow-node-notes-input" />
                </div>
                <Button size="sm" variant="outline" className="w-full rounded-sm" onClick={() => duplicateNode(selNode)} data-testid="flow-duplicate-node"><Copy className="mr-1 h-4 w-4" /> Duplicate node</Button>
                {isMaterialKind(selNode.data.kind) && (
                  <Button size="sm" variant="outline" className="w-full rounded-sm" onClick={() => setTrace(traceFrom(selNode.id))} data-testid="flow-trace-button"><Route className="mr-1 h-4 w-4" /> Trace this material</Button>
                )}
                <ConnList title="Incoming" items={selConns.incoming} />
                <ConnList title="Outgoing" items={selConns.outgoing} />
              </div>
            )}

            {selEdge && (
              <div className="space-y-3 p-4" data-testid="flow-edge-form">
                <p className="text-xs text-slate-500">{nodeName(selEdge.source)} → {nodeName(selEdge.target)}</p>
                {[["material", "Material"], ["quantity", "Quantity"], ["unit", "Unit"], ["frequency", "Frequency"], ["transport", "Transportation"]].map(([k, lbl]) => (
                  <div key={k} className="space-y-1.5">
                    <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{lbl}</Label>
                    <Input value={selEdge.data?.[k] || ""} onChange={(e) => updateEdgeData(selEdge.id, { [k]: e.target.value })} className="rounded-sm" data-testid={`flow-edge-${k}`} />
                  </div>
                ))}
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Notes</Label>
                  <Textarea value={selEdge.data?.notes || ""} onChange={(e) => updateEdgeData(selEdge.id, { notes: e.target.value })} className="rounded-sm" data-testid="flow-edge-notes" />
                </div>
                <Button size="sm" variant="outline" className="w-full rounded-sm text-red-600 hover:text-red-700" onClick={() => { setEdges((eds) => eds.filter((e) => e.id !== selEdge.id)); setSelected(null); }} data-testid="flow-delete-edge"><X className="mr-1 h-4 w-4" /> Delete connection</Button>
              </div>
            )}
          </div>
        )}

        {nodes.length === 0 && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <p className="rounded-sm bg-white/80 px-4 py-2 text-sm text-slate-400">Click <b>Add node</b> to start mapping. Drag from a node's handle to connect.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function ConnList({ title, items }) {
  return (
    <div>
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title} ({items.length})</p>
      {items.length === 0 ? (
        <p className="text-xs text-slate-400">None</p>
      ) : (
        <ul className="space-y-1">
          {items.map((it, i) => (
            <li key={`${it.label}-${i}`} className="rounded-sm bg-slate-50 px-2 py-1 text-xs text-slate-700">{it.label}{it.material ? <span className="text-slate-400"> · {it.material}</span> : null}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function FlowEditor(props) {
  return (
    <ReactFlowProvider>
      <Editor {...props} />
    </ReactFlowProvider>
  );
}
